import {ILayerView, LayerInfo, LayerUpdateMetadata} from "@esWebAPI/es/model/Layer";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import {Map} from "azure-maps-control";
import _, {every} from "lodash";
import {keyedDebounce} from "@esWebAPI/es/util/KeyedDebouncer";
import {IEventChannel} from "@esWebAPI/es/model/Models";
import {AzureCriteriaAdapter} from "@esWebAPI/es/util/AzureCriteriaAdapter";
import {ColorThematic, SymbolThematic} from "@esWebAPI/es/model/Thematic";
import {ExpressionFilterSpecification} from "@maplibre/maplibre-gl-style-spec";
import {isPieClusteringOn} from "./azure/AzureLayer";
import {NavUtils} from "@esWebAPI/es/util/NavUtils";
import {ActionType, IEditResult} from "@esWebAPI/es/model/Feature";
import $ from "jquery";
import {AzureMouseoverLabelHandler} from "@esWebAPI/es/map/handler/AzureMouseoverLabelHandler";

export interface IMapLayer {
    layerView: ILayerView;
    onReady: JQuery.Deferred<void>
    destroy(): void;
    getLayerIds(): string[];
    hide(): void;
    show(): void;
}

export abstract class MapLayer implements IMapLayer {

    private _layerView: ILayerView;
    private _layerInfo: LayerInfo;

    protected readonly map: Map;
    protected readonly datasourceManager: DatasourceManager;

    private readonly _eventChannel: IEventChannel;

    protected _layerFilter: ExpressionFilterSpecification|undefined;

    protected _onReady: JQuery.Deferred<void> = $.Deferred();

    private debouncedOnUpdateLayer = keyedDebounce(this.onUpdateLayer.bind(this),(lv) => String(lv.getId()), 500);
    private debouncedOnEditFeatures = keyedDebounce(this.onEditFeatures.bind(this),(e) => String(e[0].action.layerViewId), 500);

    protected _mouseoverLabelHandler: AzureMouseoverLabelHandler|undefined;

    private _destroyed: boolean = false;

    protected constructor(map: Map, layerView: ILayerView, layerInfo: LayerInfo,
                          datasourceManager: DatasourceManager, eventChannel: IEventChannel) {

        this.map = map;
        this._layerView = layerView;
        this._layerInfo = layerInfo;
        this.datasourceManager = datasourceManager;
        this._eventChannel = eventChannel;

        eventChannel.addListener("onUpdateLayer", this.debouncedOnUpdateLayer);
        eventChannel.addListener("onUpdateFeature", this.onUpdateFeature, this);
        eventChannel.addListener("onRevertFeatureEdits", this.showFeatures, this);

        es.api.getEventChannel().addListener("onEditFeatures", this.debouncedOnEditFeatures, this);
        es.api.getEventChannel().addListener("onBatchEditFeatures", this.onBatchEditFeatures, this);
    }

    /** Checks if 2 override (color) thematics are the same ranges
     * (i.e. ignores colors, just checks hideFeatures and val as that is rendered differently)
     */
    private isSameThematicRanges(t1: any, t2: any) {
        if (!t1 && !t2) {
            return true;
        }

        if ((t1 && !t2) || (!t1 && t2)) {
            return false;
        }

        if (t1.$type != t2.$type) { // should never happen... just in case
            return false;
        }

        if (t1.$type == 'NoneColorThematic') {
            return true;
        }

        const withoutColor = (r: any) => {
            return {
                hideFeatures: r.hideFeatures,
                val: r.val
            };
        };
        const t1Ranges = t1.ranges.map(withoutColor);
        const t2Ranges = t2.ranges.map(withoutColor);

        return _.isEqual(t1Ranges, t2Ranges);
    }

    private getLayerUpdateMetadata(layerViewModel: LayerViewModel, updateEventNames: string[]): LayerUpdateMetadata {
        const layerView = layerViewModel.getLayerView();
        const prevAttrs = this.datasourceManager.getRequiredAttributes(this.layerView);
        const newAttrs = this.datasourceManager.getRequiredAttributes(layerView);
        const isMissingAttributes = !every(newAttrs, attr => prevAttrs.includes(attr));

        const prevLabelling = this.layerView.styleSettings?.defaultStyle?.text;
        const newLabelling = layerView.styleSettings?.defaultStyle?.text;
        const isLabellingChanged = !es.util.object.equals(prevLabelling, newLabelling);
        // TODO: Do isSameOverrideThematicRanges for symbol as well
        const isThematicChanged =
            !_.isEqual(this.layerView.styleSettings.colorThematicSettings?.enabled, layerView.styleSettings.colorThematicSettings?.enabled) ||
            !_.isEqual(this.layerView.styleSettings.symbolThematicSettings?.enabled, layerView.styleSettings.symbolThematicSettings?.enabled) ||
            !_.isEqual(this.layerView.styleSettings.colorThematicSettings?.attributeName, layerView.styleSettings.colorThematicSettings?.attributeName) ||
            !this.isSameThematicRanges(this.layerView.styleSettings.colorThematicSettings?.overrideThematic, layerView.styleSettings.colorThematicSettings?.overrideThematic) ||
            !_.isEqual(this.layerView.styleSettings.sizeThematicSettings?.attributeName, layerView.styleSettings.sizeThematicSettings?.attributeName) ||
            !_.isEqual(this.layerView.styleSettings?.symbolThematicSettings, layerView.styleSettings?.symbolThematicSettings);

        const isCriteriaChanged = !_.isEqual(this.layerView.criteria, layerView.criteria) || updateEventNames?.includes('onSetRemovableCriteria');
        const isFeatureInfoLastModifiedChanged = this._layerInfo.featureInfo.lastModified !== layerViewModel.getLayerInfo().featureInfo.lastModified;

        const isPieClusteringEnabledInTempModel = !!(layerView?.styleSettings.defaultStyle?.clustering || layerView?.styleSettings.defaultStyle?.symbolStyle?.clustering);
        const isPieClusteringChanged = isPieClusteringOn(this._layerView) !== isPieClusteringEnabledInTempModel;

        const isMouseoverLabellingChanged = this.layerView.styleSettings.defaultStyle.mouseover !== layerView.styleSettings.defaultStyle.mouseover;

        const isHeatmapOverlayPinsChanged = this.layerView.styleSettings?.heatMapSettings?.showFeatures !== layerView.styleSettings?.heatMapSettings?.showFeatures;

        const rs = {
            isMissingAttributes,
            isLabellingChanged,
            isThematicChanged,
            isCriteriaChanged,
            isFeatureInfoLastModifiedChanged,
            isPieClusteringChanged,
            isMouseoverLabellingChanged,
            isHeatmapOverlayPinsChanged
        } as LayerUpdateMetadata;

        es.log.log("getLayerUpdateMetadata", rs);

        return rs;
    }

    private onUpdateFeature(action: any) {
        if (this.layerView.id !== action.feature.layerViewId) {
            return;
        }
        this.hideFeature(action.feature.id);
    }

    private async onEditFeatures(editResults: IEditResult<ActionType>[]) {
        if (this.destroyed) {
            return;
        }
        const results = editResults.filter( e => e.action.layerViewId === this.layerView.id);
        if (results.length) {
            this._layerInfo = this.datasourceManager.layerModule.getById(this.layerView.id).getLayerInfo();
            await this.update({isFeatureInfoLastModifiedChanged: true});
            this.showFeatures();
        }
    }

    private onBatchEditFeatures(_numFeaturesUpdated: number, layerViewId: number) {
        if (this.layerView.id !== layerViewId) {
            return;
        }
        const lvm = this.datasourceManager.layerModule.getById(layerViewId);
        this._layerView = lvm.getLayerView();
        this._layerInfo = lvm.getLayerInfo();
        this.update({isFeatureInfoLastModifiedChanged: true});
    }

    private onUpdateLayer(layerViewModel: LayerViewModel, _isTransient: boolean, updateEventNames: string[]) {
        if (this._layerView.id !== layerViewModel.getLayerView().id || this._destroyed){
            return;
        }

        const layerUpdateMetaData = this.getLayerUpdateMetadata(layerViewModel, updateEventNames);

        this._layerView = layerViewModel.getLayerView();
        this._layerInfo = layerViewModel.getLayerInfo();

        this.update(layerUpdateMetaData);
    }

    public abstract update(layerUpdateMetadata: LayerUpdateMetadata): Promise<void>;

    protected getDatasourceFilter(colorThematic?: ColorThematic, symbolThematic?: SymbolThematic): ExpressionFilterSpecification | undefined {

        let criteria = es.util.object.deepCopy(this._layerView.criteria);

        // Nearest criteria is done on the server
        const nearestCriteria = es.util.criteria.find(this._layerView.criteria, "Nearest");
        if (nearestCriteria) {
            criteria = es.util.criteria.remove(criteria, nearestCriteria);
        }

        // Interacts criteria is done on the server
        const interactsCriteria = es.util.criteria.find(this._layerView.criteria, "Interacts");
        if (interactsCriteria) {
            criteria = es.util.criteria.remove(criteria, interactsCriteria);
        }

        // WithinDistance criteria is done on the server
        const withinDistanceCriteria = es.util.criteria.find(this._layerView.criteria, {$type: "WithinDistance"});
        if (withinDistanceCriteria) {
            criteria = es.util.criteria.remove(criteria, {$type: "WithinDistance"});
        }

        //Remove InCriteria of scheduler
        const scheduleInCriteria = es.util.criteria.find(criteria, {$type: "In", attr: "ES_ID"});
        if (scheduleInCriteria) {
            criteria = es.util.criteria.remove(criteria, scheduleInCriteria);
        }

        //Convert 0,1 boolean values to real boolean
        this._layerInfo.attributes.filter(a => a.attrType === "BOOLEAN").forEach(a => {
            const criterion = es.util.criteria.findAll(criteria,{attr: a.name})
            criterion.forEach((crit: any) => {
                if(crit.value === "") {
                    return;
                }
                if (crit.value === undefined) {
                    crit.value = "";
                } else {
                    crit.value = (crit.value === 1 || String(crit.value).toLowerCase() === "true");
                }
            });
        });

        const layerCriteriaFilter = AzureCriteriaAdapter.convert(criteria, this.map);
        const colorFilter = this.getColorThematicFilter(colorThematic);
        const symbolThematicFilter = this.getSymbolThematicFilter(symbolThematic);

        return AzureCriteriaAdapter.merge(layerCriteriaFilter, colorFilter, symbolThematicFilter);

    }

    protected abstract getColorThematicFilter(colorThematic?: ColorThematic): ExpressionFilterSpecification | undefined;

    protected abstract getSymbolThematicFilter(symbolThematic?: SymbolThematic): ExpressionFilterSpecification | undefined;

    protected abstract hideFeature(featureId: number): void;

    protected abstract showFeatures(): void;

    /**
     * Get Internal ids of layers, From bottom to top
     */
    public abstract getLayerIds(): string[];

    public destroy(): void {
        this._destroyed = true;
        this._eventChannel.removeListener("onUpdateLayer", this.debouncedOnUpdateLayer);
        this._eventChannel.removeListener("onUpdateFeature", this.onUpdateFeature, this);
        this._eventChannel.removeListener("onRevertFeatureEdits", this.showFeatures, this);

        es.api.getEventChannel().removeListener("onEditFeatures", this.debouncedOnEditFeatures, this);
        es.api.getEventChannel().removeListener("onBatchEditFeatures", this.onBatchEditFeatures, this);
    }

    public isVisibleRes(): boolean {

        if (this.layerView.minResolution == 0 && this.layerView.maxResolution == 0) {
            return true;
        }

        const minZoom = NavUtils.resolutionToZoom(this.layerView.maxResolution);
        const maxZoom = this.layerView.minResolution > 0 ?
            NavUtils.resolutionToZoom(this.layerView.minResolution) :
            this.map.getCamera().maxZoom;

        const zoom = this.map.getCamera().zoom || 0;
        return maxZoom ? zoom < maxZoom && zoom >= minZoom : zoom >= minZoom;
    }

    public abstract show(): void;

    public abstract hide(): void;

    public get layerView(): ILayerView {
        return this._layerView;
    }

    public get layerInfo(): LayerInfo {
        return this._layerInfo;
    }

    public get eventChannel(): IEventChannel {
        return this._eventChannel;
    }

    public get onReady(): JQuery.Deferred<void> {
        return this._onReady;
    }

    public get destroyed(): boolean {
        return this._destroyed;
    }

    public setLayerFilter(value: ExpressionFilterSpecification | undefined) {
        this._layerFilter = value;
    }

    /**
     * Get the active azure layer
     * todo: unfortunate to expose this in this way but its existence we need to interact with it at some point or another. There might be a better way though.
     */
    public abstract getActiveAzureLayerId(): string | undefined;
}
